import { useCallback, useEffect, useState } from 'react';
import { CAlert, CBadge, CButton, CFormCheck, CFormSelect, CFormTextarea } from '@coreui/react';
import { api, problemMessage, type Schemas } from '../../api/client';
import { Section } from '../../components/Section';
import { formatDateTime } from '../../format';

type Question = Schemas['QuestionResponse'];
type Role = Schemas['DomainRoleResponse'];

/**
 * Dotazy a úkoly. Obojí umí zakázku zastavit; liší se tím, co se čeká — odpověď, nebo
 * dodělaná práce (ADR-014). Adresuje se roli: dorazí tomu, kdo ji na zakázce zastává,
 * a když nikdo, všem, kdo tu nálepku nosí.
 */
export function QuestionsSection({ contractId, version, onChanged }: {
  contractId: number; version: number; onChanged: () => void;
}) {
  const [questions, setQuestions] = useState<Question[] | null>(null);

  const load = useCallback(() => {
    api.GET('/api/v1/contracts/{contractId}/questions', { params: { path: { contractId } } })
      .then(({ data }) => setQuestions(data ?? []));
  }, [contractId]);

  useEffect(load, [load, version]);

  if (!questions) return null;

  const open = questions.filter((q) => q.status === 'Open');
  const closed = questions.filter((q) => q.status !== 'Open');

  return (
    <Section
      title="Dotazy a úkoly"
      count={open.length}
      addLabel="Dotaz nebo úkol"
      tour="add-question"
      empty={questions.length === 0}
      form={(close) => <AskForm contractId={contractId} onDone={() => { close(); onChanged(); }} onCancel={close} />}
    >
      {open.map((q) => <QuestionRow key={q.id} contractId={contractId} question={q} onAnswered={onChanged} />)}
      {closed.length > 0 && (
        <details>
          <summary className="text-body-secondary small">Vyřízené ({closed.length})</summary>
          {closed.map((q) => <QuestionRow key={q.id} contractId={contractId} question={q} onAnswered={onChanged} />)}
        </details>
      )}
    </Section>
  );
}

function AskForm({ contractId, onDone, onCancel }: { contractId: number; onDone: () => void; onCancel: () => void }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [kind, setKind] = useState<'Question' | 'Task'>('Question');
  const [body, setBody] = useState('');
  const [role, setRole] = useState('');
  const [blocks, setBlocks] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.GET('/api/v1/team/roles').then(({ data }) => setRoles(data ?? [])); }, []);

  async function ask() {
    const { error: problem, response } = await api.POST('/api/v1/contracts/{contractId}/questions', {
      params: { path: { contractId } },
      body: { body, targetRoleCode: role || null, targetMemberId: null, contractItemId: null, blocksProgress: blocks, kind },
    });

    if (response.ok) onDone(); else setError(problemMessage(problem));
  }

  return (
    <>
      <div className="d-flex gap-3 mb-2">
        <CFormCheck type="radio" name="kind" id="kind-q" label="Dotaz" checked={kind === 'Question'} onChange={() => setKind('Question')} />
        <CFormCheck type="radio" name="kind" id="kind-t" label="Úkol" checked={kind === 'Task'} onChange={() => setKind('Task')} />
      </div>
      <CFormTextarea rows={2} className="mb-2" value={body} onChange={(e) => setBody(e.target.value)} autoFocus
        placeholder={kind === 'Task' ? 'Co je potřeba dodělat' : 'Na co se ptáte?'} />
      <div className="d-flex gap-2 align-items-center flex-wrap">
        <CFormSelect size="sm" style={{ maxWidth: 200 }} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">Komu…</option>
          {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
        </CFormSelect>
        <CFormCheck id="blocks" label="Do vyřízení zastaví zakázku" checked={blocks} onChange={(e) => setBlocks(e.target.checked)} />
        <div className="ms-auto d-flex gap-2">
          <CButton size="sm" color="secondary" variant="ghost" onClick={onCancel}>Zpět</CButton>
          <CButton size="sm" color="primary" disabled={!body.trim() || !role} onClick={() => void ask()}>Odeslat</CButton>
        </div>
      </div>
      {error && <CAlert color="danger" className="mt-2 mb-0">{error}</CAlert>}
    </>
  );
}

function QuestionRow({ contractId, question, onAnswered }: { contractId: number; question: Question; onAnswered: () => void }) {
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isTask = question.kind === 'Task';

  async function submit() {
    const { error: problem, response } = await api.POST('/api/v1/contracts/{contractId}/questions/{questionId}/answer', {
      params: { path: { contractId, questionId: question.id } },
      body: { body: answer },
    });

    if (response.ok) {
      setAnswer('');
      onAnswered();
    } else {
      setError(problemMessage(problem));
    }
  }

  return (
    <div className="mb-3">
      <div>
        <CBadge color={isTask ? 'primary' : 'secondary'} className="me-2">{isTask ? 'úkol' : 'dotaz'}</CBadge>
        {question.blocksProgress && question.status === 'Open' && <CBadge color="danger" className="me-2">blokuje</CBadge>}
        {question.body}
      </div>
      <div className="small text-body-secondary">
        {question.askedBy}{question.targetRoleCode && ` → ${question.targetRoleCode}`} · {formatDateTime(question.askedAt)}
      </div>
      {question.answerBody && (
        <div className="small mt-1 ps-2 border-start">
          {question.answerBody} <span className="text-body-secondary">· {formatDateTime(question.answeredAt)}</span>
        </div>
      )}
      {question.status === 'Open' && (
        <div className="d-flex gap-2 mt-2">
          <input className="form-control form-control-sm" data-tour="answer-question" value={answer} onChange={(e) => setAnswer(e.target.value)}
            placeholder={isTask ? 'Co je hotovo — třeba „nasazeno"' : 'Odpověď'} />
          <CButton size="sm" color="primary" variant="outline" disabled={!answer.trim()} onClick={() => void submit()}>
            {isTask ? 'Hotovo' : 'Odpovědět'}
          </CButton>
        </div>
      )}
      {error && <CAlert color="danger" className="mt-2 mb-0">{error}</CAlert>}
    </div>
  );
}
